/**
 * Article registry — single source of truth for all blog posts.
 *
 * Each entry has CA + ES versions and a `status` flag:
 *   - "published" → visible on /blog, included in sitemap, indexable
 *   - "draft"     → invisible everywhere (kept here as a reservation of slug + scaffolding)
 *
 * The `isPillar` flag marks the cornerstone activation article — it's the central
 * authority page that all category clusters link into.
 *
 * Full article bodies live in /blog/content/*.jsx as React components and are
 * attached to the registry via the `Body` field on each language version.
 * When `Body` is null the article falls back to a "coming soon" notice.
 *
 * Add a new article by appending an entry below. Re-run
 *   `node scripts/generate-sitemap.js`
 * after changing the published set so the sitemap stays in sync.
 */
import { PillarActivacioCA, PillarActivacionES } from "@/blog/content/pillar-activacion";
import { ComencarSobrepesCA, ComenzarSobrepesoES } from "@/blog/content/como-empezar-sobrepeso";
import { VergonyaGimnasCA, VerguenzaGimnasioES } from "@/blog/content/verguenza-gimnasio";
import { PerQueAbandonoCA, PorQueAbandonoES } from "@/blog/content/por-que-abandono";

export const ARTICLES = [
  // ─────────────────────────── PILLAR (activation) ───────────────────────────
  {
    id: "pillar-activacion",
    category: "activation",
    isPillar: true,
    status: "published",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-29",
    readingMinutes: 14,
    ca: {
      slug: "com-activar-te-quan-fa-anys-que-no-et-mous",
      title: "Com activar-te quan fa anys que no et mous (i ja no saps per on començar)",
      excerpt:
        "La guia central d'AI METABOLIC. Per què el teu cos no està trencat, per què cap dieta t'ha funcionat, i com tornar a moure't sense forçar-te ni avergonyir-te.",
      keyword: "com activar-me quan fa anys que no em moc",
      hero: "No és que no tinguis voluntat. És que els mètodes que has provat estan dissenyats per a persones que ja estan activades. I tu encara no ho estàs — i no passa res.",
      Body: PillarActivacioCA,
      faq: [
        {
          q: "Per què no aconsegueixo activar-me si fa anys que ho intento?",
          a: "Perquè els mètodes que has provat estan pensats per a persones que ja estan activades. Comencen suposant que pots caminar 30 minuts, que no et fa mal res, que tens temps i energia. Si el teu cos no compleix aquestes premisses, el mètode falla — no tu. Cal començar per un mètode adaptat al teu cos actual, no al que hauries de tenir.",
        },
        {
          q: "Què és la 'trampa del dilluns' i com sortir-ne?",
          a: "És el cicle de decidir el divendres que dilluns comences, fer excessos el cap de setmana, provar-ho dos dies, saltar-te'l el dimecres i posposar-ho a la setmana següent. La sortida no és 'aquesta vegada de veritat' — és canviar el mètode. Comencen dimarts, en petit, sense grans propòsits i sense compensar caps de setmana.",
        },
        {
          q: "Per què em fa vergonya moure'm en públic?",
          a: "La vergonya del cos no és feblesa: és memòria emocional acumulada de mirades, comentaris i experiències. El teu cervell guarda la vergonya al mateix lloc que el perill físic. Per això entrar a un gimnàs o sortir a caminar pot activar una resposta de por real. La solució no és forçar-te sinó començar en espais on la vergonya no s'activa (casa, hores tranquil·les, piscina municipal).",
        },
        {
          q: "Quant de temps trigo a veure resultats reals?",
          a: "Depèn del que consideris resultat. La bàscula pot trigar mesos. Però els canvis funcionals apareixen molt abans: pujar escales amb menys ofec, aixecar-te sense empènyer, dormir millor. Aquests canvis solen aparèixer entre les setmanes 3 i 6 si mantens la constància. Aquest és el veritable indicador que el cos torna a respondre.",
        },
        {
          q: "Què faig si torno a abandonar?",
          a: "Passarà. La diferència entre els que canvien i els que no és el que fan quan fallen. La regla d'or: si falles un dia, tornes l'endemà. Si falles una setmana, tornes la següent, més petit. Mai 'compenso' — sempre 'torno'. La bàscula del canvi és cumulativa, no reset.",
        },
      ],
    },
    es: {
      slug: "como-activarte-cuando-llevas-anos-sin-moverte",
      title: "Cómo activarte cuando llevas años sin moverte (y ya no sabes por dónde empezar)",
      excerpt:
        "La guía central de AI METABOLIC. Por qué tu cuerpo no está roto, por qué ninguna dieta te ha funcionado, y cómo volver a moverte sin forzarte ni avergonzarte.",
      keyword: "cómo activarme cuando llevo años sin moverme",
      hero: "No es que no tengas voluntad. Es que los métodos que has probado están diseñados para personas que ya están activadas. Y tú todavía no lo estás — y no pasa nada.",
      Body: PillarActivacionES,
      faq: [
        {
          q: "¿Por qué no consigo activarme si llevo años intentándolo?",
          a: "Porque los métodos que has probado están pensados para personas que ya están activadas. Empiezan suponiendo que puedes caminar 30 minutos, que no te duele nada, que tienes tiempo y energía. Si tu cuerpo no cumple esas premisas, el método falla — no tú. Hay que empezar por un método adaptado a tu cuerpo actual, no al que deberías tener.",
        },
        {
          q: "¿Qué es la 'trampa del lunes' y cómo salir de ella?",
          a: "Es el ciclo de decidir el viernes que el lunes empiezas, hacer excesos el fin de semana, probarlo dos días, saltártelo el miércoles y posponerlo a la semana siguiente. La salida no es 'esta vez de verdad' — es cambiar el método. Empezar un martes, en pequeño, sin grandes propósitos y sin compensar fines de semana.",
        },
        {
          q: "¿Por qué me da vergüenza moverme en público?",
          a: "La vergüenza del cuerpo no es debilidad: es memoria emocional acumulada de miradas, comentarios y experiencias. Tu cerebro guarda la vergüenza en el mismo lugar que el peligro físico. Por eso entrar a un gimnasio o salir a caminar puede activar una respuesta de miedo real. La solución no es forzarte sino empezar en espacios donde la vergüenza no se activa (casa, horas tranquilas, piscina municipal).",
        },
        {
          q: "¿Cuánto tiempo tardo en ver resultados reales?",
          a: "Depende de lo que consideres resultado. La báscula puede tardar meses. Pero los cambios funcionales aparecen mucho antes: subir escaleras con menos ahogo, levantarte sin empujarte, dormir mejor. Estos cambios suelen aparecer entre las semanas 3 y 6 si mantienes la constancia. Ese es el verdadero indicador de que el cuerpo vuelve a responder.",
        },
        {
          q: "¿Qué hago si vuelvo a abandonar?",
          a: "Pasará. La diferencia entre quienes cambian y quienes no es lo que hacen cuando fallan. La regla de oro: si fallas un día, vuelves al siguiente. Si fallas una semana, vuelves la siguiente, más pequeño. Nunca 'compenso' — siempre 'vuelvo'. La báscula del cambio es acumulativa, no reset.",
        },
      ],
    },
  },

  // ─────────────────────────── CLUSTER (priority MVP set) ───────────────────────────
  {
    id: "como-empezar-a-moverte-con-sobrepeso",
    category: "activation",
    status: "published",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-29",
    readingMinutes: 8,
    howToSteps: {
      ca: {
        name: "Com començar a moure't si tens sobrepès",
        description: "Un mètode realista de 6 passos per tornar a moure't quan fa anys que no ho fas, adaptat al teu cos d'avui.",
        totalTime: "PT7D",
        steps: [
          { name: "Comença amb 5 minuts de caminada", text: "Cinc minuts a pas normal al carrer, sense àudio ni comptar passos. L'objectiu no és cremar calories — és tornar a associar el moviment amb 'això no em fa mal'. Manté-ho una setmana." },
          { name: "Escolta el dolor amb criteri", text: "Si apareix dolor intens (7/10 o més) o s'irradia a altres zones, para i consulta un fisioterapeuta. Molèstia lleu (3-4/10) que desapareix quan pares és normal — pots continuar amb caminades més curtes." },
          { name: "Afegeix 3 exercicis de força bàsics", text: "Tres cops per setmana: aixecar-te i asseure't d'una cadira 5 vegades, empènyer una paret com si fossin flexions verticals 5 vegades, aixecar talons de puntetes 5 vegades. Dos minuts en total." },
          { name: "Estabilitza els horaris de menjar", text: "No fem dieta. Un sol canvi: esmorzar cada dia. Un iogurt amb fruita, un ou dur, una torrada. Entrenar el cos a saber que menjarà de manera previsible redueix la resistència al moviment." },
          { name: "Puja la intensitat molt gradualment", text: "Quan 5 minuts se't fa curt, puges a 7. Quan 7 se't fa curt, 10. Sempre pujant menys del que et diu el cap, més del que et diu el cos." },
          { name: "Segueix el progrés sense la bàscula", text: "Anota cada diumenge els canvis funcionals: pujar escales amb menys ofec, aixecar-te sense empènyer, dormir millor, roba que et queda diferent per dins. Aquests apareixen abans que els kg." },
        ],
      },
      es: {
        name: "Cómo empezar a moverte si tienes sobrepeso",
        description: "Un método realista de 6 pasos para volver a moverte cuando llevas años sin hacerlo, adaptado a tu cuerpo de hoy.",
        totalTime: "PT7D",
        steps: [
          { name: "Empieza con 5 minutos de caminata", text: "Cinco minutos a paso normal en la calle, sin audio ni contar pasos. El objetivo no es quemar calorías — es volver a asociar el movimiento con 'esto no me hace daño'. Mantenlo una semana." },
          { name: "Escucha el dolor con criterio", text: "Si aparece dolor intenso (7/10 o más) o se irradia a otras zonas, para y consulta a un fisioterapeuta. Molestia leve (3-4/10) que desaparece cuando paras es normal — puedes continuar con caminatas más cortas." },
          { name: "Añade 3 ejercicios de fuerza básicos", text: "Tres veces por semana: levantarte y sentarte de una silla 5 veces, empujar una pared como si fueran flexiones verticales 5 veces, levantar talones de puntillas 5 veces. Dos minutos en total." },
          { name: "Estabiliza los horarios de comida", text: "No hacemos dieta. Un solo cambio: desayunar cada día. Un yogur con fruta, un huevo duro, una tostada. Entrenar al cuerpo a saber que comerá de manera previsible reduce la resistencia al movimiento." },
          { name: "Sube la intensidad muy gradualmente", text: "Cuando 5 minutos se te haga corto, subes a 7. Cuando 7 se te haga corto, 10. Siempre subiendo menos de lo que te dice la cabeza, más de lo que te dice el cuerpo." },
          { name: "Sigue el progreso sin la báscula", text: "Anota cada domingo los cambios funcionales: subir escaleras con menos ahogo, levantarte sin empujarte, dormir mejor, ropa que te queda diferente por dentro. Estos aparecen antes que los kg." },
        ],
      },
    },
    ca: {
      slug: "com-comencar-a-moure-amb-sobrepes",
      title: "Com començar a moure't si tens sobrepès (sense gimnàs ni pressa)",
      excerpt:
        "Si fa anys que no et mous i no saps per on començar, aquí tens un mètode realista, en passos petits, pensat per al teu cos d'avui.",
      keyword: "com començar a moure'm si tinc sobrepès",
      hero: "No cal que canviïs tota la teva vida avui. Només has de fer el primer pas, i ha de ser molt més petit del que t'imagines.",
      Body: ComencarSobrepesCA,
      faq: [
        {
          q: "Puc començar a moure'm si tinc molt sobrepès i no he fet mai esport?",
          a: "Sí. La clau és començar amb 5 minuts de caminar a pas normal i tres exercicis molt bàsics de força (aixecar-se d'una cadira, empènyer una paret, aixecar talons) tres cops per setmana. L'objectiu no és cremar calories — és tornar a associar el moviment amb 'això no em fa mal'.",
        },
        {
          q: "Què faig si em fan mal els genolls quan camino?",
          a: "Si el dolor és intens (7 sobre 10 o més) o t'impedeix dormir, para i consulta un fisioterapeuta. Si és molèstia lleu (3-4 sobre 10) que desapareix quan pares, pots començar amb caminades més curtes i afegir treball de força específic pels glutis, quàdriceps i core, que són els que aguanten els genolls.",
        },
        {
          q: "Quant de temps trigaré a veure resultats?",
          a: "Depèn del que consideris resultat. Si mires la bàscula, potser mesos. Però hi ha canvis funcionals que apareixen molt abans: pujar escales amb menys ofec, aixecar-te del sofà sense empènyer, dormir millor. Aquests canvis solen aparèixer entre les setmanes 3 i 6 si mantens la constància.",
        },
        {
          q: "És millor caminar o fer força quan comences amb sobrepès?",
          a: "Al principi, tots dos junts, però la força és més important del que sembla. La força al tren inferior (cames, glutis) és el que permet que caminis més i sense dolor. Si només fas cardio sense treballar força, els genolls i l'esquena acabaran demanant explicacions.",
        },
        {
          q: "Què passa si abandono al cap d'unes setmanes?",
          a: "Passarà, i és normal. La clau no és no fallar, sinó tornar. Sense drama, sense càstig, sense 'ara compenso'. Simplement tornes a fer els 5 minuts. Un dia. Demà, un altre. La bàscula del canvi és cumulativa, no reset.",
        },
      ],
    },
    es: {
      slug: "como-empezar-a-moverte-con-sobrepeso",
      title: "Cómo empezar a moverte si tienes sobrepeso (sin gimnasio ni prisa)",
      excerpt:
        "Si llevas años sin moverte y no sabes por dónde empezar, aquí tienes un método realista, en pasos pequeños, pensado para tu cuerpo de hoy.",
      keyword: "cómo empezar a moverme si tengo sobrepeso",
      hero: "No tienes que cambiar toda tu vida hoy. Solo tienes que dar el primer paso, y debe ser mucho más pequeño de lo que imaginas.",
      Body: ComenzarSobrepesoES,
      faq: [
        {
          q: "¿Puedo empezar a moverme si tengo mucho sobrepeso y no he hecho nunca deporte?",
          a: "Sí. La clave es empezar con 5 minutos de caminar a paso normal y tres ejercicios muy básicos de fuerza (levantarse de una silla, empujar una pared, levantar talones) tres veces por semana. El objetivo no es quemar calorías — es volver a asociar el movimiento con 'esto no me hace daño'.",
        },
        {
          q: "¿Qué hago si me duelen las rodillas cuando camino?",
          a: "Si el dolor es intenso (7 sobre 10 o más) o te impide dormir, para y consulta a un fisioterapeuta. Si es molestia leve (3-4 sobre 10) que desaparece cuando paras, puedes empezar con caminatas más cortas y añadir trabajo de fuerza específico para glúteos, cuádriceps y core, que son los que sostienen las rodillas.",
        },
        {
          q: "¿Cuánto tiempo tardaré en ver resultados?",
          a: "Depende de lo que consideres resultado. Si miras la báscula, quizás meses. Pero hay cambios funcionales que aparecen mucho antes: subir escaleras con menos ahogo, levantarte del sofá sin empujarte, dormir mejor. Estos cambios suelen aparecer entre las semanas 3 y 6 si mantienes la constancia.",
        },
        {
          q: "¿Es mejor caminar o hacer fuerza cuando empiezas con sobrepeso?",
          a: "Al principio, ambos juntos, pero la fuerza es más importante de lo que parece. La fuerza en el tren inferior (piernas, glúteos) es lo que permite que camines más y sin dolor. Si solo haces cardio sin trabajar fuerza, las rodillas y la espalda acabarán pidiendo explicaciones.",
        },
        {
          q: "¿Qué pasa si abandono al cabo de unas semanas?",
          a: "Pasará, y es normal. La clave no es no fallar, sino volver. Sin drama, sin castigo, sin 'ahora compenso'. Simplemente vuelves a hacer los 5 minutos. Un día. Mañana, otro. La báscula del cambio es acumulativa, no reset.",
        },
      ],
    },
  },
  {
    id: "verguenza-gimnasio",
    category: "shame",
    status: "published",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-29",
    readingMinutes: 7,
    ca: {
      slug: "em-fa-vergonya-anar-al-gimnas",
      title: "Em fa vergonya anar al gimnàs — i no et passa només a tu",
      excerpt:
        "La vergonya és una de les barreres més grans (i menys parlades) per moure's. Aquí t'expliquem com gestionar-la sense forçar-te.",
      keyword: "em fa vergonya anar al gimnàs",
      hero: "Que et faci vergonya no vol dir que siguis feble. Vol dir que tens un cos que ha rebut mirades i comentaris durant anys.",
      Body: VergonyaGimnasCA,
      faq: [
        {
          q: "Per què em fa tanta vergonya anar al gimnàs?",
          a: "La vergonya del cos no apareix del no-res: és el resultat d'anys de mirades, comentaris i experiències acumulades. El teu cervell processa el gimnàs com una amenaça real perquè per a ell ha estat una amenaça real durant anys. No és feblesa, és memòria emocional.",
        },
        {
          q: "És millor esperar a perdre pes abans d'apuntar-me al gimnàs?",
          a: "No. Sense moure't, aquests quilos no marxaran, i si marxen amb una dieta agressiva, tornaran. La vergonya no depèn del pes, depèn de la mirada — i la mirada la portes tu. El canvi de percepció apareix quan comences a moure't, no quan arribes a un pes objectiu.",
        },
        {
          q: "Quines alternatives al gimnàs funcionen quan comences?",
          a: "Casa (5 minuts al menjador), passejos a hores tranquil·les, piscina municipal en horaris no punta, classes específiques per a cossos com el teu (iogues per a persones amb obesitat, aiguagimnàstica adaptada), o fisioterapeuta/entrenador personal en sala privada.",
        },
        {
          q: "Què faig quan la vergonya apareix de sobte mentre em moc?",
          a: "Tres coses ajuden: reconèixer-la sense discutir ('ah, tornes a ser aquí'), preguntar-te què faria una persona que no la sent i fer una versió petita del 20% que tu pots fer avui, i anotar el moment al final del dia per veure el patró al cap de quatre setmanes.",
        },
        {
          q: "La vergonya desapareix quan comences a moure't?",
          a: "No desapareix, es transforma. Primer sents vergonya de sortir a caminar amb malles, després potser pel gest de aixecar-te d'una cadira. El que canvia és la teva relació amb ella: la reconeixes abans, la deixes de negar, i comences a preguntar-te què t'està dient en lloc de com fer-la callar.",
        },
      ],
    },
    es: {
      slug: "me-da-verguenza-ir-al-gimnasio",
      title: "Me da vergüenza ir al gimnasio — y no te pasa solo a ti",
      excerpt:
        "La vergüenza es una de las barreras más grandes (y menos habladas) para moverse. Aquí te explicamos cómo gestionarla sin forzarte.",
      keyword: "me da vergüenza ir al gimnasio",
      hero: "Que te dé vergüenza no significa que seas débil. Significa que tienes un cuerpo que ha recibido miradas y comentarios durante años.",
      Body: VerguenzaGimnasioES,
      faq: [
        {
          q: "¿Por qué me da tanta vergüenza ir al gimnasio?",
          a: "La vergüenza del cuerpo no aparece de la nada: es el resultado de años de miradas, comentarios y experiencias acumuladas. Tu cerebro procesa el gimnasio como una amenaza real porque para él ha sido una amenaza real durante años. No es debilidad, es memoria emocional.",
        },
        {
          q: "¿Es mejor esperar a perder peso antes de apuntarme al gimnasio?",
          a: "No. Sin moverte, esos kilos no se irán, y si se van con una dieta agresiva, volverán. La vergüenza no depende del peso, depende de la mirada — y la mirada la llevas tú. El cambio de percepción aparece cuando empiezas a moverte, no cuando llegas a un peso objetivo.",
        },
        {
          q: "¿Qué alternativas al gimnasio funcionan cuando empiezas?",
          a: "Casa (5 minutos en el salón), paseos a horas tranquilas, piscina municipal en horarios no punta, clases específicas para cuerpos como el tuyo (yoga para personas con obesidad, aquagym adaptada), o fisioterapeuta/entrenador personal en sala privada.",
        },
        {
          q: "¿Qué hago cuando la vergüenza aparece de golpe mientras me muevo?",
          a: "Tres cosas ayudan: reconocerla sin discutir ('ah, vuelves a estar aquí'), preguntarte qué haría una persona que no la siente y hacer una versión pequeña del 20% que tú puedes hacer hoy, y anotar el momento al final del día para ver el patrón al cabo de cuatro semanas.",
        },
        {
          q: "¿La vergüenza desaparece cuando empiezas a moverte?",
          a: "No desaparece, se transforma. Primero sientes vergüenza de salir a caminar con mallas, después quizás por el gesto de levantarte de una silla. Lo que cambia es tu relación con ella: la reconoces antes, la dejas de negar, y empiezas a preguntarte qué te está diciendo en vez de cómo hacerla callar.",
        },
      ],
    },
  },
  {
    id: "por-que-abandono-siempre",
    category: "preparation",
    status: "published",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-29",
    readingMinutes: 8,
    ca: {
      slug: "per-que-abandono-sempre-el-canvi",
      title: "Per què sempre abandono — i com trencar el patró aquesta vegada",
      excerpt:
        "Abandonar un canvi no vol dir que siguis feble. Sovint el problema és el mètode, no tu. Així es trenca el cicle.",
      keyword: "per què abandono sempre",
      hero: "Si has abandonat 5 vegades, probablement el problema és que has provat 5 mètodes pensats per algú que no eres tu.",
      Body: PerQueAbandonoCA,
      faq: [
        {
          q: "Per què abandono sempre els canvis que començo?",
          a: "No perquè siguis feble. La voluntat és un recurs limitat, com la bateria del mòbil: cada decisió del dia en gasta una mica. Al final del dia, quan toca decidir 'em moc o miro la tele', la voluntat sola perd. La diferència entre els que canvien i els que no és arquitectura del mètode, no caràcter.",
        },
        {
          q: "En quin moment abandono normalment?",
          a: "Hi ha 3 punts crítics: setmana 2 (l'entusiasme s'acaba), setmana 4-6 (no veus resultats visibles), i setmana 8-10 (una cosa gran passa i trenca la rutina). Conèixer aquests punts et permet preparar-te abans d'arribar-hi.",
        },
        {
          q: "Com puc evitar el pensament 'tot o res'?",
          a: "Substituint-lo pel pensament de suma: si havies de fer 5 minuts i n'has fet 2, has fet 2 minuts. Un dia dolent no esborra una setmana bona. La bàscula del canvi és cumulativa, no reset. Amb aquesta mentalitat, els dies dolents deixen de ser el final.",
        },
        {
          q: "Què faig quan portes 3 dies sense complir el compromís?",
          a: "Aplicar el protocol de recuperació: reconèixer sense drama, investigar sense càstig (què ha passat?), baixar el llistó al 30% del que feies, i tornar demà (no la setmana que ve). Aquesta rutina de recuperació és el que separa els que abandonen dels que no.",
        },
        {
          q: "És més important tenir un objectiu o una identitat?",
          a: "Una identitat. Un objectiu (perdre pes abans de l'estiu) té data d'expiració; quan arriba o quan és evident que no arribaràs, el projecte deixa de tenir sentit. Una identitat ('vull ser algú que camina cada dia una mica') es cuida cada dia, indefinidament.",
        },
      ],
    },
    es: {
      slug: "por-que-abandono-siempre-el-cambio",
      title: "Por qué siempre abandono — y cómo romper el patrón esta vez",
      excerpt:
        "Abandonar un cambio no significa que seas débil. A menudo el problema es el método, no tú. Así se rompe el ciclo.",
      keyword: "por qué abandono siempre",
      hero: "Si has abandonado 5 veces, probablemente el problema es que has probado 5 métodos pensados para alguien que no eras tú.",
      Body: PorQueAbandonoES,
      faq: [
        {
          q: "¿Por qué abandono siempre los cambios que empiezo?",
          a: "No porque seas débil. La voluntad es un recurso limitado, como la batería del móvil: cada decisión del día gasta un poco. Al final del día, cuando toca decidir 'me muevo o miro la tele', la voluntad sola pierde. La diferencia entre quienes cambian y quienes no es arquitectura del método, no carácter.",
        },
        {
          q: "¿En qué momento abandono normalmente?",
          a: "Hay 3 puntos críticos: semana 2 (el entusiasmo se acaba), semana 4-6 (no ves resultados visibles), y semana 8-10 (una cosa grande pasa y rompe la rutina). Conocer estos puntos te permite prepararte antes de llegar.",
        },
        {
          q: "¿Cómo puedo evitar el pensamiento 'todo o nada'?",
          a: "Sustituyéndolo por el pensamiento de suma: si tenías que hacer 5 minutos y has hecho 2, has hecho 2 minutos. Un día malo no borra una semana buena. La báscula del cambio es acumulativa, no reset. Con esta mentalidad, los días malos dejan de ser el final.",
        },
        {
          q: "¿Qué hago cuando llevo 3 días sin cumplir el compromiso?",
          a: "Aplicar el protocolo de recuperación: reconocer sin drama, investigar sin castigo (¿qué ha pasado?), bajar el listón al 30% de lo que hacías, y volver mañana (no la semana que viene). Esta rutina de recuperación es lo que separa a quienes abandonan de quienes no.",
        },
        {
          q: "¿Es más importante tener un objetivo o una identidad?",
          a: "Una identidad. Un objetivo (perder peso antes del verano) tiene fecha de caducidad; cuando llega o cuando es evidente que no llegarás, el proyecto deja de tener sentido. Una identidad ('quiero ser alguien que camina cada día un poco') se cuida cada día, indefinidamente.",
        },
      ],
    },
  },

  // ─────────────────────────── DRAFT (slugs reserved, not visible) ───────────────────────────
  {
    id: "recuperar-movilidad-paso-a-paso",
    category: "mobility",
    status: "draft",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-20",
    readingMinutes: 8,
    ca: {
      slug: "com-recuperar-mobilitat-pas-a-pas",
      title: "Com recuperar mobilitat pas a pas (encara que faci anys que no t'ho plantegis)",
      excerpt:
        "La mobilitat no es perd per sempre. Aquí t'expliquem com recuperar amplitud, força i confiança en els teus moviments, dia a dia.",
      keyword: "com recuperar mobilitat",
      hero: "Mobilitat no és flexibilitat extrema. És poder fer el que necessites cada dia: agafar coses, aixecar-te, caminar sense por.",
      body: "Placeholder.",
      faq: [],
    },
    es: {
      slug: "como-recuperar-movilidad-paso-a-paso",
      title: "Cómo recuperar movilidad paso a paso (aunque lleves años sin planteártelo)",
      excerpt:
        "La movilidad no se pierde para siempre. Aquí te explicamos cómo recuperar amplitud, fuerza y confianza en tus movimientos, día a día.",
      keyword: "cómo recuperar movilidad",
      hero: "Movilidad no es flexibilidad extrema. Es poder hacer lo que necesitas cada día: coger cosas, levantarte, caminar sin miedo.",
      body: "Placeholder.",
      faq: [],
    },
  },
  {
    id: "dolor-rodillas-caminar",
    category: "mobility",
    status: "draft",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-20",
    readingMinutes: 8,
    ca: {
      slug: "em-fan-mal-els-genolls-caminant",
      title: "Em fan mal els genolls quan camino — què puc fer ara",
      excerpt:
        "El dolor de genolls quan camines no és una sentència. Aquí t'expliquem què el provoca i quins passos petits poden ajudar-te.",
      keyword: "em fan mal els genolls quan camino",
      hero: "El dolor és un missatge, no un enemic. T'està dient que cal preparar-te abans de demanar-li més al cos.",
      body: "Placeholder.",
      faq: [],
    },
    es: {
      slug: "me-duelen-las-rodillas-al-caminar",
      title: "Me duelen las rodillas al caminar — qué puedo hacer ahora",
      excerpt:
        "El dolor de rodillas al caminar no es una sentencia. Aquí te explicamos qué lo provoca y qué pasos pequeños pueden ayudarte.",
      keyword: "me duelen las rodillas al caminar",
      hero: "El dolor es un mensaje, no un enemigo. Te está diciendo que hay que prepararte antes de pedirle más al cuerpo.",
      body: "Placeholder.",
      faq: [],
    },
  },
  {
    id: "volver-hacer-ejercicio-anos",
    category: "restart",
    status: "draft",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-20",
    readingMinutes: 7,
    ca: {
      slug: "tornar-a-fer-exercici-despres-anys",
      title: "Com tornar a fer exercici després d'anys sense moure't",
      excerpt:
        "Reactivar el cos després d'anys parat no és tornar a córrer. És reaprendre a confiar en el teu propi cos, sense pressa.",
      keyword: "com tornar a fer exercici després d'anys",
      hero: "El cos no s'ha trencat — només s'ha adaptat al que has fet. I es pot tornar a adaptar.",
      body: "Placeholder.",
      faq: [],
    },
    es: {
      slug: "volver-a-hacer-ejercicio-despues-de-anos",
      title: "Cómo volver a hacer ejercicio después de años sin moverte",
      excerpt:
        "Reactivar el cuerpo después de años parado no es volver a correr. Es reaprender a confiar en tu propio cuerpo, sin prisa.",
      keyword: "cómo volver a hacer ejercicio después de años",
      hero: "El cuerpo no se ha roto — solo se ha adaptado a lo que has hecho. Y puede volver a adaptarse.",
      body: "Placeholder.",
      faq: [],
    },
  },
  {
    id: "no-energia-ejercicio",
    category: "activation",
    status: "draft",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-20",
    readingMinutes: 6,
    ca: {
      slug: "no-tinc-energia-fer-exercici",
      title: "No tinc energia per fer exercici — i no és falta de voluntat",
      excerpt:
        "Si tens fatiga crònica i sents que no tens energia per moure't, no és culpa teva. Aquí t'expliquem què passa i per on començar.",
      keyword: "no tinc energia per fer exercici",
      hero: "L'energia no apareix per voluntat. Apareix quan ajustes el ritme al que el teu cos pot fer avui.",
      body: "Placeholder.",
      faq: [],
    },
    es: {
      slug: "no-tengo-energia-para-hacer-ejercicio",
      title: "No tengo energía para hacer ejercicio — y no es falta de voluntad",
      excerpt:
        "Si tienes fatiga crónica y sientes que no tienes energía para moverte, no es culpa tuya. Aquí te explicamos qué pasa y por dónde empezar.",
      keyword: "no tengo energía para hacer ejercicio",
      hero: "La energía no aparece por voluntad. Aparece cuando ajustas el ritmo a lo que tu cuerpo puede hacer hoy.",
      body: "Placeholder.",
      faq: [],
    },
  },
  {
    id: "recuperar-confianza-cuerpo",
    category: "confidence",
    status: "draft",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-20",
    readingMinutes: 7,
    ca: {
      slug: "com-recuperar-confianca-cos",
      title: "Com recuperar la confiança en el teu cos (encara que faci anys que no la sentis)",
      excerpt:
        "La confiança no torna parlant amb tu mateix. Torna fent coses petites que el teu cos respongui — i tu te n'adonis.",
      keyword: "com recuperar la confiança en el meu cos",
      hero: "La confiança és el resultat, no el punt de partida. Comences fent, i la confiança va arribant.",
      body: "Placeholder.",
      faq: [],
    },
    es: {
      slug: "como-recuperar-confianza-cuerpo",
      title: "Cómo recuperar la confianza en tu cuerpo (aunque lleves años sin sentirla)",
      excerpt:
        "La confianza no vuelve hablando contigo mismo. Vuelve haciendo cosas pequeñas que tu cuerpo responde — y tú te das cuenta.",
      keyword: "cómo recuperar la confianza en mi cuerpo",
      hero: "La confianza es el resultado, no el punto de partida. Empiezas haciendo, y la confianza va llegando.",
      body: "Placeholder.",
      faq: [],
    },
  },
];

const PUBLISHED = (a) => a.status === "published";

/** Resolve a *published* article by its language-specific slug. Drafts return null. */
export function findArticleBySlug(slug) {
  if (!slug) return null;
  for (const article of ARTICLES) {
    if (!PUBLISHED(article)) continue;
    if (article.ca.slug === slug) return { article, lang: "ca" };
    if (article.es.slug === slug) return { article, lang: "es" };
  }
  return null;
}

/** List published articles sorted by: pillar first, then publishedAt desc. */
export function listArticles() {
  return ARTICLES
    .filter(PUBLISHED)
    .sort((a, b) => {
      if (a.isPillar && !b.isPillar) return -1;
      if (!a.isPillar && b.isPillar) return 1;
      return (b.publishedAt || "").localeCompare(a.publishedAt || "");
    });
}

/** Filter published articles by category key. */
export function listArticlesByCategory(categoryKey) {
  return listArticles().filter((a) => a.category === categoryKey);
}

/** All articles regardless of status — used by admin / sitemap-generators if desired. */
export function listAllArticles() {
  return [...ARTICLES];
}
